import { auth } from './firebase';
import { API_BASE_URL } from './apiBaseUrl';

// Types for API requests and responses (matching OpenAPI spec)

// User types (from new API)
export interface UserResponseDto {
  userId: string;
  accountId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  profileImage?: string | null;
  whatsappEnabled?: boolean;
  termsAndConditionsAccepted: boolean;
  termsAcceptedAt: Date | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  userId: string;
  accountId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  termsAndConditionsAccepted?: boolean;
}

export interface UpdateUserDto {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  profileImage?: string;
  termsAndConditionsAccepted?: boolean;
}

// Legacy account types (kept for backwards compatibility)
export interface AccountResponseDto {
  accountId: string;
  userId: string;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiCategory {
  id: string;
  accountId: string; // Changed from account_id to accountId
  name: string;
  color: string;
  type: 'expense' | 'income'; // New field for category type
  monthlyBudgetLimit?: number | null; // Optional monthly spending limit
  createdAt: string; // Changed from created_at to createdAt
  updatedAt: string; // Changed from updated_at to updatedAt
}

export interface CreateCategoryRequest {
  name: string;
  color: string;
  type: 'expense' | 'income'; // New field for category type
  monthlyBudgetLimit?: number | null; // Optional monthly spending limit
}

export interface UpdateCategoryRequest {
  name?: string;
  color?: string;
  type?: 'expense' | 'income'; // New field for category type
  monthlyBudgetLimit?: number | null; // Optional monthly spending limit
}

// User info for createdByUser field
export interface CreatedByUserDto {
  userId: string;
  fullName: string;
  phoneNumber: string;
  profileImage?: string;
}

export interface ApiCategoryInfo {
  id: string;
  name: string;
  color: string;
  type: 'expense' | 'income';
  isActive: boolean;
}

export interface ApiTransaction {
  id: string;
  date: string; // Will be Date string from backend
  accountId: string;
  amount: number;
  categoryId: string;
  description: string;
  paymentType?: string | null;
  cardNumber?: string | null;
  recurringPaymentId?: string | null; // Changed from periodicPaymentId
  createdAt: string; // Changed from created_at
  updatedAt: string;
  source?: string; // 'web', 'whatsapp', 'automation', 'recurring', 'import' // Changed from updated_at
  isRecurring: boolean; // New field from backend
  recurringPayment?: RecurringPaymentInfoDto; // Optional recurring payment details
  createdByUser?: CreatedByUserDto; // User who created this transaction
  category?: ApiCategoryInfo; // Category info including deleted status
  merchantName?: string | null;
  businessName?: string | null;
}

export interface CreateTransactionRequest {
  date: string;
  amount: number;
  categoryId: string;
  description: string;
  paymentType?: string; // Optional in backend
  cardNumber?: string; // Optional in backend
  recurringPaymentId?: string; // Optional recurring payment ID
}

export interface UpdateTransactionRequest {
  date?: string;
  amount?: number;
  categoryId?: string;
  description?: string;
  paymentType?: string;
  cardNumber?: string;
  businessName?: string;
}

export interface BulkDeleteTransactionsRequest {
  transactionIds: string[];
}

export interface BulkUpdateCategoryRequest {
  transactionIds: string[];
  categoryId: string;
}

export interface BulkOperationResponse {
  success: number;
  failed: number;
  total: number;
  failedIds?: string[];
}

// New interface for recurring payment info from backend
export interface RecurringPaymentInfoDto {
  id: string;
  startDate: string;
  endDate?: string;
  totalPayments: number;
  completedPayments: number;
  remainingPayments: number;
  nextPaymentDate?: string;
  paymentSequence: number;
}

// Repeat frequency type for iOS-style scheduling
export type RepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly';

// Recurring payment interfaces
export interface ApiRecurringPayment {
  id: string;
  accountId: string;
  paymentType: 'periodic' | 'installment';
  amount: number;
  totalAmount?: number;
  categoryId: string;
  description: string;
  color: string;
  dayOfMonth: number;
  firstPaymentDate: string;
  totalPayments: number;
  remainingPayments: number;
  expirationDate: string;
  isActive: boolean;
  startDate: string; // For backward compatibility
  createdAt: string;
  updatedAt: string;
  category?: {
    id: string;
    accountId: string;
    name: string;
    color: string;
    type: string;
    createdAt: string;
    updatedAt: string;
  };
  completedPayments?: number;
  totalPaid?: number;
  remainingAmount?: number;
  progressPercentage?: number;
  // New iOS-style repeat schedule fields
  repeatFrequency?: RepeatFrequency;
  repeatInterval?: number; // e.g., 2 for every 2 weeks
  repeatOnDay?: number; // 0-6 for Sunday-Saturday (weekly)
  repeatOnDate?: number; // 1-31 for day of month (monthly/yearly)
  repeatOnMonth?: number; // 1-12 for month (yearly only)
  repeatUntil?: string; // End date (if endType is 'on')
  repeatCount?: number; // Total occurrences (if endType is 'after')
}

export interface CreateRecurringPaymentRequest {
  paymentType: 'periodic' | 'installment';
  amount: number;
  totalAmount?: number;
  categoryId: string;
  description: string;
  color: string;
  dayOfMonth: number;
  firstPaymentDate: string;
  totalPayments: number;
  expirationDate: string;
  isActive?: boolean;
  // New iOS-style repeat schedule fields
  repeatFrequency?: RepeatFrequency;
  repeatInterval?: number;
  repeatOnDay?: number;
  repeatOnDate?: number;
  repeatOnMonth?: number;
  repeatUntil?: string;
  repeatCount?: number;
}

export interface UpdateRecurringPaymentRequest {
  paymentType?: 'periodic' | 'installment';
  amount?: number;
  totalAmount?: number;
  categoryId?: string;
  description?: string;
  color?: string;
  dayOfMonth?: number;
  firstPaymentDate?: string;
  totalPayments?: number;
  // Note: remainingPayments is not included - it's calculated and managed by the server
  expirationDate?: string;
  isActive?: boolean;
  // New iOS-style repeat schedule fields
  repeatFrequency?: RepeatFrequency;
  repeatInterval?: number;
  repeatOnDay?: number;
  repeatOnDate?: number;
  repeatOnMonth?: number;
  repeatUntil?: string;
  repeatCount?: number;
}

// Generic API client
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get Firebase ID token if user is authenticated
    if (auth && auth.currentUser) {
      try {
        // Force refresh to ensure we have a valid, non-expired token
        const idToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);
        headers['Authorization'] = `Bearer ${idToken}`;
      } catch (error) {
        console.warn('⚠️ ❌ Failed to get Firebase ID token:', error);
        // Continue without token - let backend handle unauthenticated requests
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get authentication headers
    const authHeaders = await this.getAuthHeaders();
    
    const config: RequestInit = {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    };


    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ API Error: ${response.status}`, errorData);
        
                      // Handle authentication errors
              if (response.status === 401) {
                console.error('🚫 Authentication failed - token may be invalid or expired');
                console.log('🔄 User will be redirected to login page');
                
                // Throw a specific error for authentication failures
                const authError = new Error('Authentication failed - please login again');
                (authError as any).isAuthError = true;
                (authError as any).status = 401;
                throw authError;
              }
        
        const error = new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        (error as any).data = errorData;
        throw error;
      }
      
      // Handle empty responses (e.g., 204 No Content)
      const contentType = response.headers.get('content-type');
      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        return {} as T;
      }
      
      const text = await response.text();
      if (!text) {
        return {} as T;
      }
      
      const data = JSON.parse(text);
      return data;
    } catch (error) {
      console.error(`💥 API request failed: ${endpoint}`, error);
      
      // Check if this is a network error and add appropriate flags
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const networkError = new Error('Network request failed - please check your internet connection');
        (networkError as any).isNetworkError = true;
        (networkError as any).originalError = error;
        throw networkError;
      }
      
      throw error;
    }
  }

  // Generic CRUD methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Upload file with FormData (doesn't set Content-Type, let browser handle it)
  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get authentication headers but don't include Content-Type
    const authHeaders: Record<string, string> = {};
    if (auth && auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        authHeaders['Authorization'] = `Bearer ${idToken}`;
      } catch (error) {
        console.warn('⚠️ Failed to get Firebase ID token:', error);
      }
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ API Error: ${response.status}`, errorData);
        
        if (response.status === 401) {
          const authError = new Error('Authentication failed - please login again');
          (authError as any).isAuthError = true;
          (authError as any).status = 401;
          throw authError;
        }
        
        const error = new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
        (error as any).status = response.status;
        (error as any).data = errorData;
        throw error;
      }
      
      const contentType = response.headers.get('content-type');
      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        return {} as T;
      }
      
      return await response.json();
    } catch (error) {
      console.error(`💥 API upload failed: ${endpoint}`, error);
      throw error;
    }
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Category API methods (aligned with OpenAPI spec)
export const categoryApi = {
  // Get all categories (account ID extracted from Firebase token)
  getAll: (): Promise<ApiCategory[]> =>
    apiClient.get('/categories'),

  // Get categories with transaction count
  getWithTransactionCount: (): Promise<any[]> =>
    apiClient.get('/categories/with-transaction-count'),

  // Get category by ID
  getById: (id: string): Promise<ApiCategory> =>
    apiClient.get(`/categories/${id}`),

  // Create new category
  create: (data: CreateCategoryRequest): Promise<ApiCategory> =>
    apiClient.post('/categories', data),

  // Update category (using PATCH as per OpenAPI)
  update: (id: string, data: UpdateCategoryRequest): Promise<ApiCategory> =>
    apiClient.patch(`/categories/${id}`, data),

  // Delete category
  delete: (id: string): Promise<void> =>
    apiClient.delete(`/categories/${id}`),
};

// Transaction API methods (aligned with OpenAPI spec)
export const transactionApi = {
  // Get all transactions (account ID extracted from Firebase token)
  getAll: (params?: { from?: string; to?: string }): Promise<ApiTransaction[]> => {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    const queryString = queryParams.toString();
    return apiClient.get(`/transactions${queryString ? `?${queryString}` : ''}`);
  },

  // Get transactions by category ID
  getByCategoryId: (categoryId: string): Promise<ApiTransaction[]> =>
    apiClient.get(`/transactions/category/${categoryId}`),

  // Get transaction by ID
  getById: (id: string): Promise<ApiTransaction> =>
    apiClient.get(`/transactions/${id}`),

  // Create new transaction
  create: (data: CreateTransactionRequest): Promise<ApiTransaction> =>
    apiClient.post('/transactions', data),

  // Update transaction (using PATCH as per OpenAPI)
  update: (id: string, data: UpdateTransactionRequest): Promise<ApiTransaction> =>
    apiClient.patch(`/transactions/${id}`, data),

  // Delete transaction
  delete: (id: string): Promise<void> =>
    apiClient.delete(`/transactions/${id}`),

  // Bulk delete transactions
  bulkDelete: (transactionIds: string[]): Promise<BulkOperationResponse> =>
    apiClient.post('/transactions/bulk/delete', { transactionIds }),

  // Bulk update transaction categories
  bulkUpdateCategory: (transactionIds: string[], categoryId: string): Promise<BulkOperationResponse> =>
    apiClient.patch('/transactions/bulk/category', { transactionIds, categoryId }),
};

// Recurring Payments API methods (new from OpenAPI spec)
export const recurringPaymentApi = {
  // Get all recurring payments (account ID extracted from Firebase token)
  getAll: (): Promise<ApiRecurringPayment[]> =>
    apiClient.get('/recurring-payments'),

  // Get active recurring payments
  getActive: (): Promise<ApiRecurringPayment[]> =>
    apiClient.get('/recurring-payments/active'),

  // Get expired recurring payments
  getExpired: (): Promise<ApiRecurringPayment[]> =>
    apiClient.get('/recurring-payments/expired'),

  // Get recurring payment by ID
  getById: (id: string): Promise<ApiRecurringPayment> =>
    apiClient.get(`/recurring-payments/${id}`),

  // Create new recurring payment
  create: (data: CreateRecurringPaymentRequest): Promise<ApiRecurringPayment> =>
    apiClient.post('/recurring-payments', data),

  // Update recurring payment
  update: (id: string, data: UpdateRecurringPaymentRequest): Promise<ApiRecurringPayment> =>
    apiClient.patch(`/recurring-payments/${id}`, data),

  // Delete recurring payment
  delete: (id: string): Promise<void> =>
    apiClient.delete(`/recurring-payments/${id}`),

  // Sync recurring payments for a specific month
  sync: (month: number, year: number): Promise<SyncRecurringPaymentsResponse> =>
    apiClient.post('/recurring-payments/sync', { month, year }),
};

export interface SyncRecurringPaymentItem {
  recurringPaymentId: string;
  description: string;
  amount: number;
  categoryName: string;
  status: 'created' | 'skipped_duplicate' | 'skipped_not_due' | 'error';
  error?: string;
}

export interface SyncRecurringPaymentsResponse {
  month: number;
  year: number;
  totalActive: number;
  created: number;
  skippedDuplicate: number;
  skippedNotDue: number;
  errors: number;
  details: SyncRecurringPaymentItem[];
}

// User API functions (new endpoints)
export const userApi = {
  // Get current user profile
  getCurrentUser: (): Promise<UserResponseDto> =>
    apiClient.get('/users/me'),

  // Create new user
  createUser: (data: CreateUserDto): Promise<UserResponseDto> =>
    apiClient.post('/users', data),

  // Update current user profile
  updateCurrentUser: (data: UpdateUserDto): Promise<UserResponseDto> =>
    apiClient.patch('/users/me', data),

  // Accept terms and conditions
  acceptTermsAndConditions: (): Promise<UserResponseDto> =>
    apiClient.post('/users/me/accept-terms', {}),
};

// Account API functions (updated to use new endpoint)
export const accountApi = {
  // Get or create account with user details (validates Firebase token)
  getOrCreateWithUser: (): Promise<UserResponseDto> =>
    apiClient.get('/accounts'),

  // Legacy methods (kept for backwards compatibility)
  getOrCreate: (): Promise<UserResponseDto> =>
    apiClient.get('/accounts'),

  create: (): Promise<UserResponseDto> =>
    apiClient.get('/accounts'),
};

// Health check and utility functions
export const healthApi = {
  // Check if API is healthy
  checkHealth: (): Promise<any> =>
    apiClient.get('/health'),

  // Get API information
  getApiInfo: (): Promise<any> =>
    apiClient.get('/'),
};

// Dashboard API types (matching swagger)
export interface DashboardRequestDto {
  startDate: string; // Format: YYYY-MM-DD
  endDate: string;   // Format: YYYY-MM-DD
}

export interface LatestTransactionDto {
  id: string;
  date: string;
  amount: number;
  description: string;
  categoryName: string;
  /** When API sends it, used for UI swatches; otherwise derived on FE from category breakdown. */
  categoryColor?: string;
  paymentType: string;
}

export interface MonthlyBalanceDto {
  month: string; // Format: YYYY-MM
  totalIncome: number;
  totalExpenses: number;
  balance: number;
}

export interface CategoryExpenseDto {
  // Support both camelCase (new) and lowercase (legacy) field names
  categoryId?: string;
  categoryid?: string;
  categoryName?: string;
  categoryname?: string;
  categoryColor?: string;
  categorycolor?: string;
  total: string;
  transactionCount?: number;
  totalexpenses?: string; // Legacy field name for transaction count
}

export interface DashboardResponseDto {
  totalExpenses: number;
  totalIncome: number;
  balance: number;
  totalRecurringPayments: number;
  expensesByCategory: CategoryExpenseDto[]; // Changed from Record to array
  latestTransactions: LatestTransactionDto[];
  monthlyBalance: MonthlyBalanceDto[];
}

// Invitation API types (matching swagger)
export interface CreateInvitationDto {
  phoneNumber: string;
}

export interface AcceptedUserDto {
  fullName: string | null;
  email: string | null;
}

export interface InvitationResponseDto {
  invitationId: string;
  phoneNumber: string;
  accountId: string;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  acceptedUser?: AcceptedUserDto | null;
}

export interface UpdateInvitationDto {
  status: 'pending' | 'accepted' | 'expired';
}

export interface CommitmentItem {
  id: string;
  source: 'recurring' | 'cc-sync';
  description: string;
  amount: number;
  type: 'standing_order' | 'installment';
  categoryName?: string;
  categoryColor?: string;
  dayOfMonth?: number;
  installmentInfo?: string;
  provider?: string;
  cardNumber?: string;
}

export interface CommitmentsResponse {
  standingOrders: CommitmentItem[];
  standingOrdersTotal: number;
  installments: CommitmentItem[];
  installmentsTotal: number;
}

// Dashboard API methods (new from OpenAPI spec)
export const dashboardApi = {
  getDashboardData: (data: DashboardRequestDto): Promise<DashboardResponseDto> =>
    apiClient.post('/dashboard', data),
  getCommitments: (): Promise<CommitmentsResponse> =>
    apiClient.get('/dashboard/commitments'),
};

// WhatsApp API functions
export const whatsappApi = {
  // Disable WhatsApp integration
  disableIntegration: (): Promise<{ success: boolean; message: string }> =>
    apiClient.post('/whatsapp/disable', {}),
  
  // Verify WhatsApp connection - checks if the account has WhatsApp enabled
  verifyConnection: (): Promise<{ whatsappEnabled: boolean }> =>
    apiClient.get('/whatsapp/verify'),
};

// Account Settings API types and functions
export interface AccountSettingsDto {
  accountId: string;
  whatsappEnabled: boolean;
  transactionNotificationsEnabled: boolean;
  monthlyBudgetLimit: number | null; // Optional monthly spending limit for entire account
  averageMonthlyExpenses: number | null; // AI-calculated average monthly expenses
  averageMonthlyIncome: number | null; // AI-calculated average monthly income
  suggestedBudgetLimit: number | null; // AI-suggested budget (75% of income)
  averageMonthsCount: number | null; // Number of months used to calculate the averages
  createdAt: string;
  updatedAt: string;
}

export interface UpdateAccountSettingsDto {
  whatsappEnabled?: boolean;
  transactionNotificationsEnabled?: boolean;
  monthlyBudgetLimit?: number | null; // Optional monthly spending limit for entire account
  onboardingCompleted?: boolean;
}

export const accountSettingsApi = {
  // Get account settings
  getSettings: (): Promise<AccountSettingsDto> =>
    apiClient.get('/accounts/settings'),
  
  // Update account settings
  updateSettings: (data: UpdateAccountSettingsDto): Promise<AccountSettingsDto> =>
    apiClient.patch('/accounts/settings', data),
};

// Contact API types and functions (aligned with backend schema)
export interface ContactDto {
  text: string;           // The message text content
  from: string;           // Email address to respond to  
  phoneNumber?: string;   // Optional phone number
}

export interface ContactResponseDto {
  message: string;        // Success message
  success: boolean;       // Indicates if the operation was successful
}

export const contactApi = {
  // Send contact message - no authentication required
  sendMessage: (data: ContactDto): Promise<ContactResponseDto> =>
    apiClient.post('/contact', data),
};

// Account Member Types
export interface AccountMemberDto {
  userId: string;
  fullName: string | null;
  email: string | null;
  phoneNumber: string | null;
  profileImage: string | null;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
}

export interface AccountMembersResponseDto {
  members: AccountMemberDto[];
  total: number;
}

// Account Members API
export const accountMembersApi = {
  getMembers: (): Promise<AccountMembersResponseDto> =>
    apiClient.get('/users/account-members'),
  
  removeMember: (userId: string): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(`/users/account-members/${userId}`),
};

// Invitation API methods (matching swagger)
export const invitationApi = {
  // Send invite to user by phone number
  createInvitation: (data: CreateInvitationDto): Promise<InvitationResponseDto> =>
    apiClient.post('/invitations', data),

  // Get all invitations sent by authenticated user
  getAllInvitations: (): Promise<InvitationResponseDto[]> =>
    apiClient.get('/invitations'),

  // Get invitation by ID
  getInvitation: (invitationId: string): Promise<InvitationResponseDto> =>
    apiClient.get(`/invitations/${invitationId}`),

  // Update invitation status
  updateInvitation: (invitationId: string, data: UpdateInvitationDto): Promise<InvitationResponseDto> =>
    apiClient.patch(`/invitations/${invitationId}`, data),

  // Delete invitation
  deleteInvitation: (invitationId: string): Promise<void> =>
    apiClient.delete(`/invitations/${invitationId}`),
};

// All API methods now use Firebase token authentication
// Backend extracts account ID from the token
// No need to pass account ID explicitly

// ===============================
// ADMIN API Types and Functions
// ===============================

// Admin Dashboard Stats
export interface AdminDashboardStats {
  totalAccounts: number;
  totalUsers: number;
  totalTransactions: number;
  newAccountsLast30Days: number;
  newUsersLast30Days: number;
  newTransactionsLast30Days: number;
  whatsappEnabledAccounts: number;
  notificationsEnabledAccounts: number;
}

// Admin Account type (includes extra fields)
export interface AdminAccountDto {
  accountId: string;
  phoneNumber: string;
  whatsappEnabled: boolean;
  transactionNotificationsEnabled: boolean;
  googleSheetsEnabled: boolean;
  userCount: number;
  usersCount?: number; // Alternative field name
  transactionCount: number;
  transactionsCount?: number; // Alternative field name
  categoriesCount: number;
  onboardingCompleted: boolean;
  welcomeEmailSentAt: string | null; // null = not sent
  activitySummaryEmailSentAt: string | null; // null = not sent
  engagementEmailSentAt: string | null; // null = not sent
  lastTransactionDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// Admin User type (includes Firebase data)
export interface AdminUserDto {
  oderedByDesc?: boolean;
  userId: string;
  accountId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  role?: 'owner' | 'admin' | 'member';
  whatsappEnabled: boolean;
  termsAndConditionsAccepted: boolean;
  termsAcceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Firebase data
  lastSignInTime?: string;
  firebaseLastSignIn?: string; // Alternative field name
  firebaseCreatedAt?: string;
}

export interface UpdateAdminAccountDto {
  whatsappEnabled?: boolean;
  transactionNotificationsEnabled?: boolean;
}

// Admin Transaction type
export interface AdminTransactionDto {
  id: string;
  date: string;
  amount: number;
  description: string;
  categoryName: string;
  categoryId: string;
  categoryColor?: string;
  source?: string;
  createdAt: string;
  createdByUserName: string | null;
}

export interface AdminTransactionsResponse {
  transactions: AdminTransactionDto[];
  total: number;
}

// Admin Category type
export interface AdminCategoryDto {
  id: string;
  name: string;
  color: string;
  type: 'income' | 'expense';
  transactionsCount: number;
  createdAt: string;
}

export interface AdminCategoriesResponse {
  categories: AdminCategoryDto[];
  total: number;
}

// Admin API client with x-admin-api-key header
const adminApiClient = {
  get: async <T>(endpoint: string): Promise<T> => {
    const adminApiKey = import.meta.env.VITE_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('Admin API key not configured');
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-api-key': adminApiKey,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Admin API error: ${response.status}`);
    }
    
    return response.json();
  },
  
  patch: async <T>(endpoint: string, data: unknown): Promise<T> => {
    const adminApiKey = import.meta.env.VITE_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('Admin API key not configured');
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-api-key': adminApiKey,
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`Admin API error: ${response.status}`);
    }
    
    return response.json();
  },
  
  delete: async (endpoint: string): Promise<void> => {
    const adminApiKey = import.meta.env.VITE_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('Admin API key not configured');
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-api-key': adminApiKey,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Admin API error: ${response.status}`);
    }
  },
  
  post: async <T>(endpoint: string, data: unknown): Promise<T> => {
    const adminApiKey = import.meta.env.VITE_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('Admin API key not configured');
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-api-key': adminApiKey,
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`Admin API error: ${response.status}`);
    }
    
    return response.json();
  },
};

// Admin API methods
export const adminApi = {
  // Dashboard
  getDashboardStats: (): Promise<AdminDashboardStats> =>
    adminApiClient.get('/admin/dashboard/stats'),
  
  // Accounts
  getAllAccounts: (): Promise<AdminAccountDto[]> =>
    adminApiClient.get('/admin/accounts'),
  
  getAccountById: (accountId: string): Promise<AdminAccountDto> =>
    adminApiClient.get(`/admin/accounts/${accountId}`),
  
  updateAccount: (accountId: string, data: UpdateAdminAccountDto): Promise<AdminAccountDto> =>
    adminApiClient.patch(`/admin/accounts/${accountId}`, data),
  
  deleteAccount: (accountId: string, deleteFirebaseUsers: boolean = false): Promise<void> =>
    adminApiClient.delete(`/admin/accounts/${accountId}${deleteFirebaseUsers ? '?deleteFirebaseUsers=true' : ''}`),
  
  // Users
  getAllUsers: (): Promise<AdminUserDto[]> =>
    adminApiClient.get('/admin/users'),
  
  getUsersByAccount: (accountId: string): Promise<AdminUserDto[]> =>
    adminApiClient.get(`/admin/accounts/${accountId}/users`),

  getUserById: (userId: string): Promise<AdminUserDto> =>
    adminApiClient.get(`/admin/users/${userId}`),
  
  // Transactions
  getAccountTransactions: (accountId: string, limit: number = 10): Promise<AdminTransactionsResponse> =>
    adminApiClient.get(`/admin/accounts/${accountId}/transactions?limit=${limit}`),
  
  // Categories
  getAccountCategories: (accountId: string): Promise<AdminCategoriesResponse> =>
    adminApiClient.get(`/admin/accounts/${accountId}/categories`),
  
  // Email
  sendWelcomeEmail: (accountId: string): Promise<{ message: string; email: string }> =>
    adminApiClient.post(`/admin/accounts/${accountId}/send-welcome-email`, {}),
  
  sendActivitySummaryEmail: (accountId: string): Promise<{ message: string; email: string }> =>
    adminApiClient.post(`/admin/accounts/${accountId}/send-activity-summary-email`, {}),
  
  sendEngagementEmail: (accountId: string): Promise<{ message: string; email: string }> =>
    adminApiClient.post(`/admin/accounts/${accountId}/send-engagement-email`, {}),
};

// Google Sheets Integration Types
export interface GoogleSheetsIntegrationDto {
  id: string;
  accountId: string;
  enabled: boolean;
  spreadsheetId: string | null;
  sheetName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGoogleSheetsIntegrationDto {
  enabled?: boolean;
  spreadsheetId?: string;
  sheetName?: string;
}

export interface UpdateGoogleSheetsIntegrationDto {
  enabled?: boolean;
  spreadsheetId?: string;
  sheetName?: string;
}

export interface GoogleSheetsServiceAccountInfo {
  email: string;
  instructions: string;
}

export interface SheetInfo {
  name: string;
  sheetId: number;
}

export interface GoogleSheetsTestResult {
  success: boolean;
  spreadsheetName?: string;
  sheetName?: string;
  availableSheets?: SheetInfo[];
  error?: string;
}

export interface GoogleSheetsTestInput {
  spreadsheetUrl: string;
}

// Google Sheets Integration API
export const googleSheetsApi = {
  getServiceAccountInfo: (): Promise<GoogleSheetsServiceAccountInfo> =>
    apiClient.get('/google-sheets/service-account'),

  getIntegration: (): Promise<GoogleSheetsIntegrationDto | null> =>
    apiClient.get('/google-sheets'),

  createOrUpdateIntegration: (
    data: CreateGoogleSheetsIntegrationDto
  ): Promise<GoogleSheetsIntegrationDto> =>
    apiClient.post('/google-sheets', data),

  updateIntegration: (
    data: UpdateGoogleSheetsIntegrationDto
  ): Promise<GoogleSheetsIntegrationDto> =>
    apiClient.patch('/google-sheets', data),

  deleteIntegration: (): Promise<void> => apiClient.delete('/google-sheets'),

  testConnection: (data: GoogleSheetsTestInput): Promise<GoogleSheetsTestResult> =>
    apiClient.post('/google-sheets/test', data),
};

// Utility function to check if backend is available
export const checkBackendConnection = async (): Promise<boolean> => {
  try {
    await healthApi.checkHealth();
    return true;
  } catch (error) {
    console.warn('Backend not available:', error);
    return false;
  }
};

// Transaction Import Types
export interface ParsedTransaction {
  date: string;
  amount: number;
  description: string;
  suggestedCategory?: string;
  categoryId?: string;
  categoryName?: string;
  originalText?: string;
  confidence?: number;
  paymentType?: string;
  installmentNumber?: number;
  totalInstallments?: number;
}

export interface ImportAnalysisResponse {
  success: boolean;
  transactions: ParsedTransaction[];
  totalCount: number;
  fileName?: string;
  fileType?: string;
  warnings?: string[];
  error?: string;
}

export interface ConfirmTransaction {
  date: string;
  amount: number;
  description: string;
  categoryId: string;
  paymentType?: string;
}

export interface ConfirmImportRequest {
  transactions: ConfirmTransaction[];
  notifyUsers?: boolean;
}

export interface ImportResult {
  success: boolean;
  importedCount: number;
  transactionIds: string[];
  error?: string;
}

export interface SupportedFormats {
  extensions: string[];
  mimeTypes: string[];
  maxFileSize: number;
}

// Transaction Import API
// Category inference types
export interface CategoryExample {
  description: string;
  amount: number;
}

export interface InferredCategory {
  name: string;
  nameEn: string;
  color: string;
  type: 'expense' | 'income';
  examples: CategoryExample[];
  transactionCount: number;
  selected: boolean;
}

export interface CategoryInferenceResponse {
  success: boolean;
  categories: InferredCategory[];
  totalTransactions: number;
  fileName?: string;
  error?: string;
}

export const transactionImportApi = {
  // Get supported file formats
  getSupportedFormats: (): Promise<SupportedFormats> =>
    apiClient.get('/transaction-import/supported-formats'),

  // Upload and analyze file
  analyzeFile: (file: File): Promise<ImportAnalysisResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.postFormData('/transaction-import/analyze', formData);
  },

  // Confirm and import transactions
  confirmImport: (data: ConfirmImportRequest): Promise<ImportResult> =>
    apiClient.post('/transaction-import/confirm', data),

  // Infer categories from file (for onboarding)
  inferCategories: (file: File): Promise<CategoryInferenceResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.postFormData(
      '/transaction-import/infer-categories',
      formData
    );
  },
};

// Credit Card Sync Types
export type CreditCardProvider = 'isracard' | 'max' | 'visaCal';

export interface CreditCardConnectionDto {
  id: string;
  accountId: string;
  provider: CreditCardProvider;
  displayName: string;
  enabled: boolean;
  defaultCategoryId: string | null;
  selectedCards: string[] | null;
  lastSyncAt: string | null;
  lastSyncStatus: string;
  lastSyncError: string | null;
  syncCount: number;
  lastSyncTransactionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCreditCardConnectionRequest {
  provider: CreditCardProvider;
  displayName: string;
  credentials: Record<string, string>;
  defaultCategoryId?: string;
  selectedCards?: string[];
}

export interface UpdateCreditCardConnectionRequest {
  displayName?: string;
  enabled?: boolean;
  defaultCategoryId?: string;
  credentials?: Record<string, string>;
  selectedCards?: string[];
}

export interface PotentialDuplicate {
  existingTransactionId: string;
  existingDescription: string;
  existingAmount: number;
  existingDate: string;
  existingSource?: string;
  ccDescription: string;
  ccAmount: number;
  ccDate: string;
  ccExternalId: string;
  ccCardNumber?: string;
  ccPaymentType?: string;
  ccMerchantName?: string;
}

export interface DuplicateResolution {
  existingTransactionId: string;
  action: 'replace' | 'keep_both';
  ccExternalId: string;
  ccDescription: string;
  ccAmount: number;
  ccDate: string;
  ccCardNumber?: string;
  ccPaymentType?: string;
  ccMerchantName?: string;
  connectionId: string;
}

export interface RecurringOverlap {
  recurringPaymentId: string;
  recurringDescription: string;
  recurringAmount: number;
  categoryName?: string;
  paymentType?: string;
  dayOfMonth?: number;
}

export interface CreditCardSyncResult {
  connectionId: string;
  provider: string;
  success: boolean;
  transactionsFound: number;
  transactionsImported: number;
  transactionsSkipped: number;
  error?: string;
  potentialDuplicates?: PotentialDuplicate[];
  recurringOverlaps?: RecurringOverlap[];
  noHistory?: boolean;
}

export interface TestCreditCardConnectionRequest {
  provider: CreditCardProvider;
  credentials: Record<string, string>;
}

export interface DiscoveredCard {
  accountNumber: string;
  transactionCount: number;
}

export interface TestCreditCardConnectionResult {
  success: boolean;
  error?: string;
  cards?: DiscoveredCard[];
}

// Credit Card Sync API
export const creditCardSyncApi = {
  getConnections: (): Promise<CreditCardConnectionDto[]> =>
    apiClient.get('/credit-card-sync/connections'),

  getConnection: (id: string): Promise<CreditCardConnectionDto> =>
    apiClient.get(`/credit-card-sync/connections/${id}`),

  createConnection: (data: CreateCreditCardConnectionRequest): Promise<CreditCardConnectionDto> =>
    apiClient.post('/credit-card-sync/connections', data),

  updateConnection: (id: string, data: UpdateCreditCardConnectionRequest): Promise<CreditCardConnectionDto> =>
    apiClient.patch(`/credit-card-sync/connections/${id}`, data),

  deleteConnection: (id: string): Promise<void> =>
    apiClient.delete(`/credit-card-sync/connections/${id}`),

  testConnection: (data: TestCreditCardConnectionRequest): Promise<TestCreditCardConnectionResult> =>
    apiClient.post('/credit-card-sync/test-connection', data),

  sync: (connectionId?: string, daysBack?: number): Promise<CreditCardSyncResult[]> => {
    const body: Record<string, any> = {};
    if (connectionId) body.connectionId = connectionId;
    if (daysBack !== undefined) body.daysBack = daysBack;
    return apiClient.post('/credit-card-sync/sync', body);
  },

  resolveDuplicates: (resolutions: DuplicateResolution[]): Promise<{ replaced: number; kept: number }> =>
    apiClient.post('/credit-card-sync/resolve-duplicates', { resolutions }),

  deactivateRecurring: (recurringPaymentIds: string[]): Promise<{ deactivated: number }> =>
    apiClient.post('/credit-card-sync/deactivate-recurring', { recurringPaymentIds }),

  isFeatureEnabled: (): Promise<{ enabled: boolean }> =>
    apiClient.get('/credit-card-sync/feature-enabled'),
};